import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import Typography from '@material-ui/core/Typography'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import Grid from '@material-ui/core/Grid'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import filterFactory, { textFilter, selectFilter, numberFilter } from 'react-bootstrap-table2-filter'
import LeadModal from 'components/admin/LeadModal'
import AllocateLeadModal from 'components/admin/AllocateLeadModal'
import { getProductString, getTooltipContent, getMoment, getLeadDisplayName } from 'config/helpers/Helpers'
import { NotificationManager } from 'react-notifications'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import componentStylesButtons from 'assets/theme/components/button.js'
import Tooltip from '@mui/material/Tooltip'
import moment from 'moment-timezone'

const useStyles = makeStyles(componentStyles)
const useStylesButtons = makeStyles(componentStylesButtons)

function LeadsTable(props) {
    const bstable: any = useRef()
    const classes = { ...useStyles(), ...useStylesButtons() }
    const theme = useTheme()
    const api = new AdminAPI()

    const [lead, setLead] = useState(null)
    const [showLeadModal, setShowLeadModal] = useState(false)
    const [showAllocateLeadModal, setShowAllocateLeadModal] = useState(false)
    const [showBulkRejectModal, setShowBulkRejectModal] = useState(false)
    const [selected, setSelected] = useState([])
    const [loading, setLoading] = useState(false)
    const [bulkRejectLoading, setBulkRejectLoading] = useState(false)
    const [alert, setAlert] = useState(false)

    const selectLead = useCallback((lead) => {
        setSelected(prev => [...prev, lead])
    }, [])

    const deselectLead = useCallback((lead) => {
        setSelected(prev => prev.filter(item => !(item.phone === lead.phone && item.product === lead.product)))
    }, [])

    function deselectAll() {
        setSelected([])
        setAlert(null)
    }

    const selectRow = useMemo(() => ({
        mode: 'checkbox',
        selected: selected.map(s => s.leadId),
        selectColumnStyle: { textAlign: 'center', margin: 'auto', width: '50px' },
        onSelect: (row, isSelect) => {
            if (isSelect) {
                selectLead(row)
            }
            else {
                deselectLead(row)
            }
        },
        onSelectAll: (isSelect, rows) => {
            if (isSelect) {
                setSelected(rows)
            }
            else {
                setSelected([])
            }
        }
    }), [selected, selectLead, deselectLead])

    function getType(lead) {
        if (lead.product === 'pensions') {
            return lead.quote.type
        }
        else if (lead.product === 'income-protection') {
            return lead.quote.riskLevel
        }
        else {
            return '-'
        }
    }

    function formatTrackingValue(value) {
        if (!value) return '-'
        if (typeof value === 'string') return value
        return JSON.stringify(value)
    }

    const columns = [
        {
            dataField: 'firstName',
            text: 'Name',
            //sort: true,
            filter: textFilter(),
            filterValue: (cell, row) => `${row.firstName || ''} ${row.lastName || ''}`.trim(),
            formatter: (cell, row) => {
                return (
                    <Tooltip title={getTooltipContent(row)} placement='right' arrow>
                        <Box className='hover-text primary-text' onClick={() => openLeadModal(row)}>
                            {getLeadDisplayName(row.firstName, row.lastName)}
                        </Box>
                    </Tooltip>
                )
            },
            headerFormatter: (col, colIndex, components) => {
                return components.filterElement
            }
        },
        {
            dataField: 'phone',
            text: 'Phone',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{row.phone}</Box>)
            },
        },
        {
            dataField: 'email',
            text: 'Email',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{row.email}</Box>)
            },
        },
        {
            dataField: 'isValid',
            text: 'Passed Validation',
            sort: true,
            //filter: numberFilter(),
            formatter: (cell, row) => {
                if ((row.queued || row.aiQueued) && !row.companyId) {
                    const target = row.queuedCompanyName || (row.aiQueued ? 'AI' : null)
                    return (
                        <Box
                            component='span'
                            px={1}
                            py={0.25}
                            borderRadius={4}
                            fontSize='12px'
                            fontWeight={600}
                            bgcolor={theme.palette.warning.badgeBg}
                            color={theme.palette.warning.badge}
                        >
                            {target ? `Queued → ${target}` : 'Queued'}
                        </Box>
                    )
                }
                return <>{row.isValid ? 'Yes' : 'No (' + row.invalidReason + ')'}</>
            }
        },
        {
            dataField: 'source',
            text: 'Source',
            sort: true,
            //filter: numberFilter(),
            formatter: (cell, row) => {
                return <>{row.source}</>
            }
        },
        {
            dataField: 'adSource',
            text: 'Ad Source',
            sort: true,
            formatter: (cell, row) => {
                return <>{formatTrackingValue(row.adSource)}</>
            }
        },
        {
            dataField: 'createdAt',
            text: props.showStatus === 'Allocated' ? 'Allocated' : 'Created',
            sort: true,
            formatter: (cell, row) => {
                return <>{moment(row.createdAt).format('DD/MM HH:mm')}</>
                //return <>{moment().diff(row.createdAt, 'days')} days ago</>
                //return <>{moment(row.createdAt).fromNow()}</>
            }
        }
    ]

    if (props.showStatus === 'Allocated') {
        columns.splice(columns.length - 1, 0, {
            dataField: 'createdAtOriginal',
            text: 'Created',
            sort: true,
            formatter: (cell, row) => {
                return <>{moment(row.createdAtOriginal).format('DD/MM HH:mm')}</>
                //return <>{moment().diff(row.createdAt, 'days')} days ago</>
                //return <>{moment(row.createdAt).fromNow()}</>
            }
        })
        columns.splice(2, 0, {
            dataField: 'company',
            text: 'Company',
            sort: true,
            formatter: (cell, row) => {
                return <>{row.companyId ? row.companyId.toUpperCase() : '-'}</>
            }
        })
        columns.splice(3, 0, {
            dataField: 'autoAllocated',
            text: 'Auto Allocated',
            sort: true,
            formatter: (cell, row) => {
                return <>{row.autoAllocated ? 'Yes' : 'No'}</>
            }
        })
        columns.splice(4, 0, {
            dataField: 'leadPrice',
            text: 'Lead Price',
            sort: true,
            formatter: (cell, row) => {
                return <>{row.leadPrice != null ? '€' + row.leadPrice : '-'}</>
            }
        })
    }

    if (props.product === 'all') {
        columns.splice(1, 0, {
            dataField: 'product',
            text: 'Product',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{getProductString(row.product)}</Box>)
            },
        })
    }

    if (props.product === 'pensions' || props.product === 'all') {
        columns.splice(2, 0, {
            dataField: 'type',
            text: 'Type',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{getType(row)}</Box>)
            }
        })
        columns.splice(3, 0, {
            dataField: 'currentValue',
            text: 'Pension Value',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{row.quote.currentValue || '-'}</Box>)
            }
        })
    } 

    if (props.product === 'income-protection') {
        columns.splice(2, 0, {
            dataField: 'risk',
            text: 'Risk',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{row.quote.riskLevel || '-'}</Box>)
            }
        })
        columns.splice(3, 0, {
            dataField: 'dob',
            text: 'DOB',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{row.dob || '-'}</Box>)
            }
        })
        columns.splice(4, 0, {
            dataField: 'salary',
            text: 'Salary',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{'€' + row.salary || '-'}</Box>)
            }
        })
        columns.splice(5, 0, {
            dataField: 'healthIssue',
            text: 'Health Issue',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{row.majorHealthIssue || '-'}</Box>)
            }
        })
    } 

    if (props.product === 'life-insurance') {
        columns.splice(2, 0, {
            dataField: 'dob',
            text: 'DOB',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{row.dob || '-'}</Box>)
            }
        })
        columns.splice(3, 0, {
            dataField: 'healthIssue',
            text: 'Health Issue',
            sort: true,
            formatter: (cell, row) => {
                return (<Box>{row.majorHealthIssue || '-'}</Box>)
            }
        })
    } 

    function openAllocateLeadModal() {
        let containsPension = false
        for (let lead of selected) {
            if (lead.product === 'pensions') {
                containsPension = true
            }
        }
        setShowAllocateLeadModal(true)
    }

    function openBulkRejectModal() {
        setShowBulkRejectModal(true)
    }

    async function bulkRejectLeads() {
        setBulkRejectLoading(true)
        try {
            // Call bulk API endpoint
            const leadIds = selected.map(lead => ({leadId: lead.leadId, date: lead.date}))
            const response = await api.rejectMultiple(leadIds)
            console.log('response: ', response)
            // response: { success: [id, ...], failure: [{leadId, reason}, ...] }
            let successCount = response.successes ? response.successes.length : 0
            let failureCount = response.failures ? response.failures.length : 0
            let msg = `Rejected ${successCount} lead(s)`
            if (failureCount > 0) {
                msg += `. ${failureCount} failed.`
            }
            props.showSuccessAlert(msg)
            if (props.refreshLeads) props.refreshLeads()
            deselectAll()
        } catch(e) {
            onError(e)
            NotificationManager.error('Error rejecting leads')
        }
        setBulkRejectLoading(false)
        setShowBulkRejectModal(false)
    }

    function openLeadModal(lead) {
        setLead(lead)
        setShowLeadModal(true)
    }

    function closeLeadModal() {
        setShowLeadModal(false)
        setLead(null)
    }

    return (
        <Card>
            {alert}
            {!loading ? (
                <>
                    <CardHeader
                        subheader={
                            <>
                                <Box display='flex' style={{justifyContent: 'space-between'}}>
                                    <Box>
                                        <Box
                                            component={Typography}
                                            variant='h2'
                                            marginBottom='0!important'
                                        >
                                            <Box component='span'>{props.title} Leads ({props.leads.length})</Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </>
                        }
                        classes={{ root: classes.cardHeaderRoot }}
                    ></CardHeader>

                    <div className='table-container'>
                        <BootstrapTable 
                            ref={bstable}
                            keyField='leadId' 
                            data={ props.leads }
                            columns={ columns } 
                            filter={ filterFactory() } 
                            selectRow={ selectRow }
                            pagination={ props.leads.length > 100 ? paginationFactory({
                                sizePerPage: 100,
                                hideSizePerPage: true,
                                showTotal: true,
                                hidePageListOnlyOnePage: true,
                                paginationSize: 5,
                                onPageChange: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
                                paginationTotalRenderer: (from, to, size) => (
                                    <span className='react-bootstrap-table-pagination-total' style={{marginLeft: '0.75rem'}}>
                                        Showing rows {from} to {to} of {size}
                                    </span>
                                ),
                            }) : undefined }
                            noDataIndication='No pending leads'
                        />
                    </div>

                    <Grid container>
                        {selected.length > 0 &&
                            <>
                                <Grid item xs={12} style={{margin: '0.5rem'}}>
                                    <Box display='flex' alignItems='center' justifyContent='space-between'>
                                        <Button
                                            component={Box}
                                            onClick={() => openAllocateLeadModal()}
                                            color='primary'
                                            variant='contained'
                                            size='medium'
                                            //style={{marginRight: '1rem'}}
                                        >
                                            Allocate {selected.length} Leads
                                        </Button>

                                        <Button
                                            component={Box}
                                            classes={{
                                                root: classes.buttonContainedError,
                                            }}
                                            onClick={() => openBulkRejectModal()}
                                            color='warning'
                                            variant='contained'
                                            size='medium'
                                        >
                                            Reject {selected.length} Leads
                                        </Button>
                                    </Box>
                                </Grid>
                            </>
                        }
                    </Grid>
                    {/* Bulk Reject Modal */}
                    {showBulkRejectModal && (
                        <ReactBSAlert
                            warning
                            style={{ display: 'block', marginTop: '-100px' }}
                            title='Are you sure?'
                            onConfirm={bulkRejectLeads}
                            onCancel={() => setShowBulkRejectModal(false)}
                            cancelBtnCssClass='btn-secondary'
                            confirmBtnBsStyle='danger'
                            cancelBtnText='Cancel'
                            confirmBtnText={`Yes, Reject ${selected.length} Leads`}
                            disabled={bulkRejectLoading}
                            showCancel
                            btnSize=''
                        >
                            This will move {selected.length} lead(s) to 'Rejected'. They can still be allocated later.
                            <Box style={{marginTop: '2rem', marginBottom: '1rem', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                                {bulkRejectLoading ? <Loader height={12} /> : null}
                            </Box>
                        </ReactBSAlert>
                    )}
                    {/* ...existing code... */}
                    {showAllocateLeadModal &&
                        <AllocateLeadModal 
                            isOpen={showAllocateLeadModal}
                            close={() => setShowAllocateLeadModal(false)}
                            leads={selected}
                            showSuccessAlert={(text) => props.showSuccessAlert(text)}
                            companies={props.companies}
                            filteredCompanies={props.filteredCompanies}
                            allCompanies={props.allCompanies}
                            capacityMap={props.capacityMap}
                            updateLead={(lead) => props.updateLead(lead)}
                            removeLeads={props.removeLeads}
                            deselectAll={() => deselectAll()}
                        />
                    }
                    {showLeadModal &&
                        <LeadModal 
                            isOpen={showLeadModal}
                            close={() => closeLeadModal()}
                            lead={lead}
                            refreshLeads={() => props.refreshLeads()}
                            showSuccessAlert={(text) => props.showSuccessAlert(text)}
                            companies={props.companies}
                            filteredCompanies={props.filteredCompanies}
                            allCompanies={props.allCompanies}
                            capacityMap={props.capacityMap}
                            updateLead={(lead) => props.updateLead(lead)}
                            admin={props.admin}
                        />
                    }
                </>
            ) : (
                <Grid container justifyContent='flex-start'>
                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='center' alignItems='center' style={{height: '20vh'}}>
                            <Loader />
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Card>
    );
}

export default LeadsTable